import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { Plus, Calendar, MapPin, Users, Edit, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import PageLayout from '../components/PageLayout';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const TripsManagement = () => {
  const { user, logout } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTrip, setEditTrip] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    location: 'Antigua, Guatemala',
    description: '',
    max_participants: 35,
    status: 'planning'
  });

  // Auto-generate trip name from start date
  const generateTripName = (startDate) => {
    if (!startDate) return '';
    const d = new Date(startDate + 'T00:00:00');
    const month = d.toLocaleString('en-US', { month: 'long' });
    const year = d.getFullYear();
    // Count existing trips in the same month/year to determine week number
    const sameMonthTrips = trips.filter(t => {
      const td = new Date(t.start_date + 'T00:00:00');
      return td.getMonth() === d.getMonth() && td.getFullYear() === d.getFullYear();
    });
    // If editing, don't count the current trip
    const existingCount = editTrip
      ? sameMonthTrips.filter(t => t.trip_id !== editTrip.trip_id).length
      : sameMonthTrips.length;
    const weekNum = existingCount + 1;
    return `${month} ${year} - Week ${weekNum}`;
  };

  const handleStartDateChange = (newDate) => {
    const autoName = generateTripName(newDate);
    const nameWasAuto = !formData.name || formData.name === generateTripName(formData.start_date);
    setFormData({
      ...formData,
      start_date: newDate,
      name: nameWasAuto ? autoName : formData.name,
    });
  };

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      const res = await api.get('/trips');
      setTrips(res.data);
    } catch (error) {
      toast.error('Failed to load trips');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editTrip) {
        await api.put(`/trips/${editTrip.trip_id}`, formData);
        toast.success('Trip updated!');
      } else {
        await api.post('/trips', formData);
        toast.success('Trip created!');
      }
      setShowCreate(false);
      setEditTrip(null);
      setFormData({
        name: '',
        start_date: '',
        end_date: '',
        location: 'Antigua, Guatemala',
        description: '',
        max_participants: 35,
        status: 'planning'
      });
      fetchTrips();
    } catch (error) {
      toast.error('Failed to save trip');
    }
  };

  const handleEdit = (trip) => {
    setEditTrip(trip);
    setFormData({
      name: trip.name,
      start_date: trip.start_date,
      end_date: trip.end_date,
      location: trip.location,
      description: trip.description || '',
      max_participants: trip.max_participants,
      status: trip.status
    });
    setShowCreate(true);
  };

  const handleDelete = async (tripId) => {
    if (!window.confirm('Are you sure you want to delete this trip?')) return;
    
    try {
      await api.delete(`/trips/${tripId}`);
      toast.success('Trip deleted');
      fetchTrips();
    } catch (error) {
      toast.error('Failed to delete trip');
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const tripDialog = (
    <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) setEditTrip(null); }}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-rojo hover:bg-rojo-dark" data-testid="create-trip-btn">
          <Plus className="w-4 h-4 mr-2" />New Trip
        </Button>
      </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading">{editTrip ? 'Edit Trip' : 'Create New Trip'}</DialogTitle>
                </DialogHeader>
                <form noValidate onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Trip Name <span className="text-xs text-slate-400 font-normal ml-1">(auto-generated, editable)</span></Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="October 2027 - Week 1"
                      required
                      data-testid="trip-name-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => handleStartDateChange(e.target.value)}
                        required
                        data-testid="trip-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                        required
                        data-testid="trip-end-date"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="Antigua, Guatemala"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Max Participants</Label>
                      <Input
                        type="number"
                        value={formData.max_participants}
                        onChange={(e) => setFormData({...formData, max_participants: parseInt(e.target.value)})}
                        min={1}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="planning">Planning</SelectItem>
                          <SelectItem value="registration_open">Registration Open</SelectItem>
                          <SelectItem value="registration_closed">Registration Closed</SelectItem>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Trip description..."
                      rows={3}
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                    <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="save-trip-btn">
                      {editTrip ? 'Update' : 'Create'} Trip
                    </Button>
                  </div>
                </form>
              </DialogContent>
    </Dialog>
  );

  return (
    <PageLayout title="Manage Trips" headerActions={tripDialog} maxWidth="max-w-6xl">
          {trips.length === 0 ? (
            <Card className="text-center py-12">
              <CardContent>
                <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-heading font-semibold text-slate-800 mb-2">No Trips Yet</h3>
                <p className="text-slate-500 mb-4">Create your first mission trip to get started.</p>
                <Button onClick={() => setShowCreate(true)} className="rounded-full">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Trip
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {trips.map((trip) => (
                <Card key={trip.trip_id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <Badge className={
                          trip.status === 'registration_open' ? 'bg-status-success/10 text-status-success mb-2' :
                          trip.status === 'active' ? 'bg-primary/10 text-primary mb-2' :
                          trip.status === 'completed' ? 'bg-slate-100 text-slate-600 mb-2' :
                          'bg-status-warning/10 text-status-warning mb-2'
                        }>
                          {trip.status.replace('_', ' ')}
                        </Badge>
                        <CardTitle className="font-heading">{trip.name}</CardTitle>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(trip)} data-testid={`edit-trip-${trip.trip_id}`}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(trip.trip_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-primary" />
                        <span>{trip.start_date} - {trip.end_date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-primary" />
                        <span>{trip.location}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-primary" />
                        <span>{trip.participant_count || 0} / {trip.max_participants} participants</span>
                      </div>
                    </div>
                    <div className="mt-4 flex gap-2">
                      <Link to={`/trips/${trip.trip_id}`} className="flex-1">
                        <Button variant="outline" className="w-full rounded-full">View Details</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
    </PageLayout>
  );
};

export default TripsManagement;
